import { EmbedContextOptions } from '@thoughtspot/blink-context';
import { Input_Eureka_WarningDetails } from '@thoughtspot/conv-assist';
import { envFlags } from '@thoughtspot/env-flags';
import { FEATURE_FLAG_NAMES, isFeatureEnabled } from '@thoughtspot/feature-service';
import { translate } from '@thoughtspot/i18n';
import { generateUUID } from '@thoughtspot/js-util';
import {
    EntityHeader,
    FalconDataType,
    PhraseType,
    RecognizedToken,
    RecognizedTokenType,
    SagePhrase,
} from '/@services/generated/graphql-types';
import {
    Action,
    shouldHideForEmbed,
} from '/@utils/embed.util';
import { checkIfPhraseIsAttributeFilter } from './edit-token-modal/edit-token-modal.utils';

export enum WarningType {
    /// /////////////////////////////////////////////////
    // some of the warning types are commented out    //
    // as they are not being used currently           //
    /// /////////////////////////////////////////////////
    ColumnChangedValueHeuristicApplied = 'COLUMN_CHANGED_VALUE_HEURISTIC_APPLIED',
    ContainsHeuristicApplied = 'CONTAINS_HEURISTIC_APPLIED',
    // EmptyInputToTml = 'EMPTY_INPUT_TO_TML',
    // NoInitialQuesInCtx = 'NO_INITIAL_QUES_IN_CTX',
    NoMatchValueHeuristicApplied = 'NO_MATCH_VALUE_HEURISTIC_APPLIED',
    // PartialTmlConversion = 'PARTIAL_TML_CONVERSION',
    // PartialTmlSanitization = 'PARTIAL_TML_SANITIZATION',
    // RandomColSelection = 'RANDOM_COL_SELECTION',
    ReplaceValueHeuristicApplied = 'REPLACE_VALUE_HEURISTIC_APPLIED',
    // SampleValueMissing = 'SAMPLE_VALUE_MISSING'

    // FE only warning types move to backend TODO
    MultipleColumnMatches = 'MULTIPLE_COLUMN_MATCHES',
    MultipleValueMatches = 'MULTIPLE_VALUE_MATCHES',
    MultipleAggregatedKeywords = 'MULTIPLE_AGGREGATED_KEYWORDS',
}

// English-only warning message templates for mixpanel logging
const ENGLISH_WARNING_TEMPLATES: Record<string, string> = {
    'Eureka.aiAnswer.warning.ColumnChangedValueHeuristicApplied':
        'Verify if "X" is the correct column for filter "X".',
    'Eureka.aiAnswer.warning.ContainsHeuristicApplied':
        'We have used "contains" because an exact match for "X" may not exist in the column "X". Select the value manually for accurate results.',
    'Eureka.aiAnswer.warning.NoMatchValueHeuristicApplied':
        'The value "X" does not exist in the column "X".',
    'Eureka.aiAnswer.warning.ReplaceValueHeuristicApplied':
        'Verify if "X" is the correct value for the column "X". Select the value manually for accurate results.',
    'Eureka.aiAnswer.warning.MultipleColumnMatches':
        'There are multiple columns which have matches for the value "X". Select the correct column.',
    'Eureka.aiAnswer.warning.MultipleValueMatches':
        'There are multiple matches for the value "X" in column "X". Select the value(s) manually for accurate results.',
};

export type SampleValueMissingDetail = {
    columns?: EntityHeader[];
};

export type Column = {
    id?: string;
    name?: string;
};

export type ColumnValuePair = {
    column?: Column;
    value?: string;
};

export type ValueHeuristicsDetails = {
    colValPairs?: ColumnValuePair[];
};

export enum AmbiguityType {
    ColumnSelection = 'COLUMN_SELECTION',
    ValueSelection = 'VALUE_SELECTION',
    KeywordSelection = 'KEYWORD_SELECTION',
}

export type AmbiguityDetails = {
    ambiguityType?: AmbiguityType;
    alternativeTokens?: RecognizedToken[];
};

export type WarningDetails = {
    sampleValueMissingDetail?: SampleValueMissingDetail;
    valueHeuristicDetail?: ValueHeuristicsDetails;
    warningType: WarningType;
    ambiguityDetails?: AmbiguityDetails;
};

type RecognizedPhrase = {
    phraseType: PhraseType;
    token: RecognizedToken[];
};

export type AmbiguousPhrase = {
    alternativePhrases: RecognizedPhrase[];
    ambiguityType: AmbiguityType;
    phraseType: PhraseType;
    token: RecognizedToken[];
};

export type ColumnValueWarningDetails = {
    guid: string;
    warningType: WarningType;
    columnId: string;
    columnName: string;
    value?: string;
    ambiguityType?: AmbiguityType;
    alternativeTokens?: RecognizedToken[];
};

export const getColumnValueWarningDetailsMap = (
    warningDetails: WarningDetails[] | Input_Eureka_WarningDetails[] = [],
): Map<string, ColumnValueWarningDetails> => {
    const guidToWarningDetails: Map<
        string,
        ColumnValueWarningDetails
    > = new Map();
    warningDetails.forEach(warningDetail => {
        if (warningDetail?.valueHeuristicDetail) {
            warningDetail.valueHeuristicDetail.colValPairs?.forEach(
                colValPair => {
                    if (colValPair && colValPair.column?.id) {
                        const guid = generateUUID();
                        guidToWarningDetails.set(guid, {
                            guid,
                            warningType: warningDetail.warningType as WarningType,
                            columnId: colValPair.column.id,
                            columnName: colValPair.column.name || '',
                            value: colValPair.value || '',
                        });
                    }
                },
            );
        }
    });
    return guidToWarningDetails;
};

export const getColumnValueWarningDetails = (
    guidToColumnValueWarningDetails: Map<
        string,
        ColumnValueWarningDetails
    > = new Map(),
    columnId: string,
    value: string,
): ColumnValueWarningDetails[] => {
    return (
        Array.from(guidToColumnValueWarningDetails.values() || []).filter(
            colValDetails =>
                colValDetails?.columnId === columnId &&
                colValDetails?.value?.replace(/^'+|'+$/g, '') ===
                    value?.replace(/^'+|'+$/g, ''),
        ) || []
    );
};

export const removeIdFromColumnValueWarningDetailsMap = (
    map: Map<string, ColumnValueWarningDetails>,
    ids: string[] = [],
): Map<string, ColumnValueWarningDetails> => {
    ids.forEach(id => {
        if (map && map.has(id)) {
            map.delete(id);
        }
    });
    return map;
};

export type GuidAndWarningMessage = {
    guid: string;
    warningMessage: string;
    hasWarning: boolean;
    hasError: boolean;
    warningHighlightTypes? : WarningHighlightType[];
    alternativeTokens?: RecognizedToken[];
    ambiguityType?: AmbiguityType;
    warningMessageTemplate?: string;
};

const defaultGuidAndWarningMessage: GuidAndWarningMessage = {
    guid: '',
    warningMessage: '',
    hasWarning: false,
    hasError: false,
    warningHighlightTypes: [],
    alternativeTokens: [],
    ambiguityType: null,
};

export enum WarningHighlightType {
    None = 'None',
    Column = 'Column',
    Operator = 'Operator',
    Value = 'Value',
    Aggregation = 'Aggregation',
}

export interface EmbedColumnParam {
    columnId?: string;
    columnName?: string;
}

export interface OpenFilterParam {
    column: EmbedColumnParam;
}

export const getWarningMessageForPhrase: (
    p: SagePhrase,
    d: Map<string, ColumnValueWarningDetails>,
) => GuidAndWarningMessage[] = (
    phrase: SagePhrase,
    guidToColumnValueWarningDetails: Map<string, ColumnValueWarningDetails>,
): GuidAndWarningMessage[] => {
    const result: GuidAndWarningMessage[] = [];
    if (
        [
            PhraseType.GroupByColumnPhrase,
            PhraseType.AggregatedColumnPhrase,
            PhraseType.FilterPhrase,
            PhraseType.FilteredCountPhrase,
            PhraseType.GeofilterPhrase,
            PhraseType.InFilterPhrase,
            PhraseType.OfFilterPhrase,
            PhraseType.InFilterSubqueryPhrase,
            PhraseType.PercentageOfFilterPhrase,
        ].includes(phrase.type)
    ) {
        if (checkIfPhraseIsAttributeFilter(phrase)) {
            const columnId = phrase.tokens[0].column.columnGuid;
            const value = phrase.tokens[2].text;

            const columnValueWarningDetails: ColumnValueWarningDetails[] = getColumnValueWarningDetails(
                guidToColumnValueWarningDetails,
                columnId,
                value,
            );

            columnValueWarningDetails.forEach(colValDetail => {
                if (colValDetail) {
                    const warningType = colValDetail.warningType;
                    const col = colValDetail.columnName;
                    const value = colValDetail.value;
                    const guid = colValDetail.guid;
                    if (
                        warningType ===
                        WarningType.ColumnChangedValueHeuristicApplied
                    ) {
                        result.push({
                            ...defaultGuidAndWarningMessage,
                            guid,
                            warningMessage: translate(
                                'Eureka.aiAnswer.warning.ColumnChangedValueHeuristicApplied',
                                { col, value },
                            ),
                            hasWarning: true,
                            warningMessageTemplate: ENGLISH_WARNING_TEMPLATES['Eureka.aiAnswer.warning.ColumnChangedValueHeuristicApplied'],
                        });
                    }

                    if (warningType === WarningType.ContainsHeuristicApplied) {
                        result.push({
                            ...defaultGuidAndWarningMessage,
                            guid,
                            warningMessage: translate(
                                'Eureka.aiAnswer.warning.ContainsHeuristicApplied',
                                { col, value },
                            ),
                            hasWarning: true,
                            warningHighlightTypes: [WarningHighlightType.Operator, WarningHighlightType.Value],
                            warningMessageTemplate: ENGLISH_WARNING_TEMPLATES['Eureka.aiAnswer.warning.ContainsHeuristicApplied'],
                        });
                    }

                    if (
                        warningType === WarningType.NoMatchValueHeuristicApplied
                    ) {
                        result.push({
                            ...defaultGuidAndWarningMessage,
                            guid,
                            warningMessage: translate(
                                'Eureka.aiAnswer.warning.NoMatchValueHeuristicApplied',
                                { col, value },
                            ),
                            hasError: true,
                            warningHighlightTypes: [WarningHighlightType.Value],
                            warningMessageTemplate: ENGLISH_WARNING_TEMPLATES['Eureka.aiAnswer.warning.NoMatchValueHeuristicApplied'],
                        });
                    }

                    if (
                        warningType === WarningType.ReplaceValueHeuristicApplied
                    ) {
                        result.push({
                            ...defaultGuidAndWarningMessage,
                            guid,
                            warningMessage: translate(
                                'Eureka.aiAnswer.warning.ReplaceValueHeuristicApplied',
                                { col, value },
                            ),
                            hasWarning: true,
                            warningHighlightTypes: [WarningHighlightType.Value],
                            warningMessageTemplate: ENGLISH_WARNING_TEMPLATES['Eureka.aiAnswer.warning.ReplaceValueHeuristicApplied'],
                        });
                    }

                    if (warningType === WarningType.MultipleColumnMatches) {
                        result.push({
                            ...defaultGuidAndWarningMessage,
                            guid,
                            warningMessage: translate(
                                'Eureka.aiAnswer.warning.MultipleColumnMatches',
                                { value },
                            ),
                            hasWarning: true,
                            warningHighlightTypes: [WarningHighlightType.Column],
                            alternativeTokens: colValDetail.alternativeTokens,
                            ambiguityType: colValDetail.ambiguityType,
                            warningMessageTemplate: ENGLISH_WARNING_TEMPLATES['Eureka.aiAnswer.warning.MultipleColumnMatches'],
                        });
                    }

                    if (warningType === WarningType.MultipleValueMatches) {
                        result.push({
                            ...defaultGuidAndWarningMessage,
                            guid,
                            warningMessage: translate(
                                'Eureka.aiAnswer.warning.MultipleValueMatches',
                                { col, value },
                            ),
                            hasWarning: true,
                            warningHighlightTypes: [WarningHighlightType.Value],
                            alternativeTokens: colValDetail.alternativeTokens,
                            ambiguityType: colValDetail.ambiguityType,
                            warningMessageTemplate: ENGLISH_WARNING_TEMPLATES['Eureka.aiAnswer.warning.MultipleValueMatches'],
                        });
                    }

                    if (
                        warningType === WarningType.MultipleAggregatedKeywords
                    ) {
                        result.push({
                            ...defaultGuidAndWarningMessage,
                            guid,
                            hasWarning: true,
                            warningHighlightTypes: [WarningHighlightType.Aggregation],
                            alternativeTokens: colValDetail.alternativeTokens,
                            ambiguityType: colValDetail.ambiguityType,
                            warningMessageTemplate: '',
                        });
                    }
                }
            });
        }
    }
    return result;
};

export type HighlightTypeResult = {
    [key in WarningHighlightType as `has${key}Error`]: boolean;
} & {
    [key in WarningHighlightType as `has${key}Warning`]: boolean;
};

export const checkHighlightTypes = (
    warningMessages: GuidAndWarningMessage[],
    highlightTypesToCheck: WarningHighlightType[],
    embedConfig: EmbedContextOptions,
): HighlightTypeResult => {
    const tokenHighlightEnabled = isFeatureEnabled(
        FEATURE_FLAG_NAMES.ENABLE_SPOTTER_ATAC_TOKEN_HIGHLIGHT,
        {
            urlParamValue: envFlags.getValue(
                FEATURE_FLAG_NAMES.ENABLE_SPOTTER_ATAC_TOKEN_HIGHLIGHT,
            ),
        },
    ) as boolean;
    const hideTokenHighlight = shouldHideForEmbed(
        embedConfig,
        Action.SpotterWarningsOnTokens,
    );
    const enabledTokenHighlight = tokenHighlightEnabled && !hideTokenHighlight;
    if (!enabledTokenHighlight) {
        return {} as HighlightTypeResult;
    }
    return highlightTypesToCheck.reduce((result: HighlightTypeResult, highlightType) => {
        const hasError = warningMessages
            .filter((message) => message.warningHighlightTypes?.includes(highlightType) && message.hasError)
            .length > 0;

        const hasWarning = warningMessages
            .filter((message) => message.warningHighlightTypes?.includes(highlightType) && message.hasWarning)
            .length > 0;

        return {
            ...result,
            [`has${highlightType}Error`]: hasError,
            [`has${highlightType}Warning`]: hasWarning,
        };
    }, {} as HighlightTypeResult);
};

// Check if a phrase is is a filter phrase for Spotter
export const isSpotterFilterSagePhrase = (phrase: SagePhrase) =>
    phrase?.type === PhraseType.VersusPhrase ||
    phrase?.type === PhraseType.VersusSubqueryPhrase;

// Check if a phrase is is a filter phrase
export const isFilterSagePhrase = (phrase: SagePhrase) =>  phrase.type === PhraseType.FilterPhrase ||
    phrase.type === PhraseType.FilteredCountPhrase ||
    // Sage emits HAVING_PHRASE (not FILTER_PHRASE) for a filter on an
    // aggregate, e.g. "sum sales < 500". It is a filter everywhere else in
    // blink - see FILTER_PHRASE_TYPES in sage-token-context-menu - so it must
    // resolve to the filter knowledge card here too (SCAL-337336).
    phrase.type === PhraseType.HavingPhrase ||
    phrase.type === PhraseType.GeofilterPhrase ||
    phrase.type === PhraseType.InFilterPhrase ||
    phrase.type === PhraseType.OfFilterPhrase ||
    phrase.type === PhraseType.InFilterSubqueryPhrase ||
    phrase.type === PhraseType.PercentageOfFilterPhrase ||
    phrase.type === PhraseType.SortByPhrase ||
    phrase.type === PhraseType.TopBottomPhrase ||
    phrase.type === PhraseType.TopBottomFilterPhrase ||
    phrase.type === PhraseType.GrowthPhrase ||
    isSpotterFilterSagePhrase(phrase);

// A filter on an aggregate ("average sales > 100"), which sage emits as
// HAVING_PHRASE. The quick-edit token modal cannot express these: its apply
// path emits ADD_PREDICATE_FILTER keyed on a column guid, and
// QueryTransformInput carries no `aggregation`, so applying it would rebuild
// the filter against the raw column instead of the aggregate. These tokens are
// routed to the measure-filter widget, which builds ADD_HAVING_FILTER
// transforms with the aggregation attached (SCAL-337336).
export const isAggregatedFilterSagePhrase = (phrase: SagePhrase) =>
    phrase?.type === PhraseType.HavingPhrase;

// The column a filter phrase applies to. Keyword tokens carry a `column` whose
// name is the keyword itself, so the first token is not reliable - take the
// first token that actually names a measure or attribute column.
export const getFilterPhraseColumnGuid = (phrase: SagePhrase): string => {
    const tokens = phrase?.tokens ?? [];
    const columnToken = tokens.find(
        token =>
            (token?.type === RecognizedTokenType.Measure ||
                token?.type === RecognizedTokenType.Attribute) &&
            !!token?.column?.columnGuid,
    );
    // Date/time filter tokens are not typed as measure or attribute, so fall
    // back to the first token that carries a column guid at all.
    return (
        columnToken?.column?.columnGuid ||
        tokens.find(token => !!token?.column?.columnGuid)?.column?.columnGuid ||
        ''
    );
};

// Check if a phrase is is a data phrase
export const isDateSagePhrase = (phrase: SagePhrase) =>  phrase.type === PhraseType.GroupByColumnPhrase &&
    phrase.tokens.some(
        token =>
            token.dataType === FalconDataType.Date ||
            token.dataType === FalconDataType.DateTime ||
            token.dataType === FalconDataType.Time,
    );

// Check if a phrase is is a measure phrase
export const isMeasureSagePhrase = (phrase: SagePhrase) => phrase.type ===
    PhraseType.AggregatedAttributeValuePhrase ||
    phrase.type === PhraseType.AggregatedColumnPhrase ||
    phrase.type === PhraseType.GrowthPhrase ||
    phrase.type === PhraseType.OfPhrase ||
    phrase.type === PhraseType.PercentageOfPhrase ||
    phrase.type === PhraseType.VersusPhrase ||
    phrase.type === PhraseType.VersusSubqueryPhrase;

// Check if a phrase is is a measure or attribute phrase
export const isMeasureOrAttributeSagePhrase = (phrase: SagePhrase) =>
phrase.type === PhraseType.GroupByColumnPhrase ||
    phrase.type === PhraseType.ForEachPhrase || // ForEachPhrase is treated as Attribute
    (isMeasureSagePhrase(phrase) && !isSpotterFilterSagePhrase(phrase));

export const getWarningHighlightTypes = (
    guidAndWarningMessages: GuidAndWarningMessage[],
): WarningHighlightType[] => {
    const messageWithHighlightTypes = guidAndWarningMessages.find(
        guidAndWarningMessage =>
            (guidAndWarningMessage.hasError || guidAndWarningMessage.hasWarning) &&
            guidAndWarningMessage.warningHighlightTypes?.length > 0
    );

    return messageWithHighlightTypes?.warningHighlightTypes || [];
};

export const getWarningMessageTemplate = (
    guidAndWarningMessages: GuidAndWarningMessage[],
): string => {
    const messageWithTemplate = guidAndWarningMessages.find(
        guidAndWarningMessage =>
            guidAndWarningMessage.warningMessageTemplate &&
            (guidAndWarningMessage.hasError || guidAndWarningMessage.hasWarning)
    );

    return messageWithTemplate?.warningMessageTemplate || '';
};

export const getMixpanelAdditionalProperties = (
    guidAndWarningMessages: GuidAndWarningMessage[],
    hasErrorStyles: boolean,
    hasWarningStyles: boolean,
): { warningMessage: string; warningType: string[] } => {
    if (!hasErrorStyles && !hasWarningStyles) {
        return {
            warningMessage: '',
            warningType: [],
        };
    }

    return {
        warningMessage: getWarningMessageTemplate(guidAndWarningMessages),
        warningType: getWarningHighlightTypes(guidAndWarningMessages),
    };
};
